import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState(localStorage.getItem('company_id') ? parseInt(localStorage.getItem('company_id')) : null);
  const [companyName, setCompanyName] = useState(localStorage.getItem('company_name') || '');
  const [role, setRole] = useState(localStorage.getItem('role') || '');
  const [userVerified, setUserVerified] = useState(sessionStorage.getItem('user_verified') === 'true');
  const [operatorName, setOperatorName] = useState(sessionStorage.getItem('operator_name') || '');
  const [isAdmin, setIsAdmin] = useState(sessionStorage.getItem('is_admin') === 'true');

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`);
      setUser(response.data);
      setRole(response.data.role || 'company');
      setCompanyId(response.data.company_id || null);
      setCompanyName(response.data.company_name || '');
    } catch (error) {
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/login`, { username, password });
      const { access_token, role: loginRole, company_id, company_name } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('role', loginRole);
      setToken(access_token);
      setRole(loginRole);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

      if (loginRole === 'company') {
        localStorage.setItem('company_id', company_id);
        localStorage.setItem('company_name', company_name);
        setCompanyId(company_id);
        setCompanyName(company_name);
      }

      await fetchUser();
      return { success: true, role: loginRole };
    } catch (error) {
      return { success: false, error: error.response?.data?.detail || 'Σφάλμα σύνδεσης' };
    }
  };

  const verifyUserPassword = async (password) => {
    try {
      const response = await axios.post(`${API_URL}/auth/verify-user-password`, { password });
      if (response.data.valid) {
        sessionStorage.setItem('user_verified', 'true');
        sessionStorage.setItem('operator_name', response.data.operator_name || '');
        sessionStorage.setItem('is_admin', response.data.is_admin ? 'true' : 'false');
        sessionStorage.setItem('operator_password', password);
        setUserVerified(true);
        setOperatorName(response.data.operator_name || '');
        setIsAdmin(response.data.is_admin || false);
        return true;
      }
      return false;
    } catch {
      return false;
    }
  };

  const enterCompany = async (companyIdToEnter) => {
    try {
      const response = await axios.post(`${API_URL}/auth/enter-company/${companyIdToEnter}`);
      const { access_token, company_id, company_name } = response.data;
      localStorage.setItem('token', access_token);
      localStorage.setItem('company_id', company_id);
      localStorage.setItem('company_name', company_name);
      setToken(access_token);
      setCompanyId(company_id);
      setCompanyName(company_name);
      sessionStorage.setItem('user_verified', 'true');
      setUserVerified(true);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      await fetchUser();
      return true;
    } catch {
      return false;
    }
  };

  const exitCompany = () => {
    // Super admin goes back to company list - re-login with super token
    localStorage.removeItem('company_id');
    localStorage.removeItem('company_name');
    sessionStorage.removeItem('user_verified');
    sessionStorage.removeItem('operator_name');
    sessionStorage.removeItem('is_admin');
    sessionStorage.removeItem('operator_password');
    setCompanyId(null);
    setCompanyName('');
    setUserVerified(false);
    setOperatorName('');
    setIsAdmin(false);
    // Need to re-login as super admin without company
    logout();
  };

  const logoutOperator = () => {
    sessionStorage.removeItem('user_verified');
    sessionStorage.removeItem('operator_name');
    sessionStorage.removeItem('is_admin');
    sessionStorage.removeItem('operator_password');
    setUserVerified(false);
    setOperatorName('');
    setIsAdmin(false);
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('company_id');
    localStorage.removeItem('company_name');
    localStorage.removeItem('role');
    sessionStorage.removeItem('user_verified');
    sessionStorage.removeItem('operator_name');
    sessionStorage.removeItem('is_admin');
    sessionStorage.removeItem('operator_password');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
    setCompanyId(null);
    setCompanyName('');
    setRole('');
    setUserVerified(false);
    setOperatorName('');
    setIsAdmin(false);
  };

  return (
    <AuthContext.Provider value={{
      user, token, login, logout, logoutOperator, loading,
      companyId, companyName, role,
      userVerified, verifyUserPassword,
      enterCompany, exitCompany,
      operatorName, isAdmin
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
